import { useRef, useState } from "react";
import { downloadBackup, restoreBackup } from "../../state/backup-actions.js";
import { useT } from "../../i18n/use-t.js";

interface BackupNotice {
  message: string;
  kind: "status" | "alert";
}

/**
 * 백업/복원 UI(결정 #6/#18). 전체 데이터(엔티티 + 에셋 바이트)를 단일 JSON으로
 * 내보내고 들여온다. 로컬 우선(결정 #1) 앱이라 기기 분실·이전 대비 수동 백업이
 * 동기화(결정 #20)가 붙기 전까지 유일한 안전망이다.
 */
export function BackupSection() {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<BackupNotice | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onExport = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await downloadBackup();
      setMsg({ message: t("backup.exportDone"), kind: "status" });
    } catch (e) {
      setMsg({ message: t("backup.exportFail", { error: e instanceof Error ? e.message : String(e) }), kind: "alert" });
    } finally {
      setBusy(false);
    }
  };

  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!confirm(t("backup.restoreConfirm"))) return;
    setBusy(true);
    setMsg(null);
    try {
      await restoreBackup(file);
      setMsg({ message: t("backup.restoreDone"), kind: "status" });
    } catch (e) {
      setMsg({ message: t("backup.restoreFail", { error: e instanceof Error ? e.message : String(e) }), kind: "alert" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("backup.title")}
      </h2>
      <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={onImport}
        />
        <p className="text-xs text-ink-faint">
          {t("backup.desc")}
        </p>
        <div className="flex gap-2">
          <button
            className="flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={onExport}
            disabled={busy}
          >
            {t("common.export")}
          </button>
          <button
            className="flex-1 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            {t("common.import")}
          </button>
        </div>
        {msg && (
          <p
            className="text-xs text-ink-soft"
            role={msg.kind}
            aria-live={msg.kind === "alert" ? "assertive" : "polite"}
            aria-atomic="true"
          >
            {msg.message}
          </p>
        )}
      </div>
    </section>
  );
}
